/**
 * JourneysDetail/constants.js
 * desc:
 * note:
 */
'use strict';

import { CodeIcon, DecisionIcon, ErrorWatchEndIcon, ErrorWatchStartIcon, ExitIcon, JunctionIcon, LinkIcon, LoopEndIcon, LoopStartIcon, RedirectIcon, RequestIcon, ResolutionIcon, ReviewsIcon, SourceIcon, TemplateIcon, TimeIcon, TransformIcon, UnknownIcon, VariableIcon } from '../../components/Icon';
import { StepCodeIllustration, StepDecisionIllustration, StepExitIllustration, StepJunctionIllustration, StepLinkIllustration, StepRequestIllustration, StepResolutionIllustration, StepReviewIllustration, StepSourceIllustration, StepTemplateIllustration, StepTransformIllustration, StepUnknownIllustration, ManWaitingIllustration, WomanWithConstantIllustration } from '../../components/Illustration';


/**
 * STEP
 * desc: configuration for workflow steps
 * note: this is the "source-of-truth" for steps throughout codebase
 * {
 *      COLOR: 'code',   // default palette key (global.css .step-palette-<key>)
 *      FLYOUT_TEMPLATE: 'CODE',
 *      ICON: <CodeIcon readOnly={ true }/>,
 *      ILLUSTRATION: <StepCodeIllustration/>,
 *      INPUTS_HIDDEN: [],
 *      INPUTS_RESERVED: null,
 *      IS_CREATABLE: false,   // used to populate Step Factories Create menu
 *      IS_SYSTEM_STEP: false,
 *      IS_TERMINATION_STEP: false,
 *      IS_VIRTUAL_STEP: false,
 *      MENU_OPTS: [ 'COPY', 'CUT', 'DELETE', 'EDIT' ],
 *      PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
 *      SWAP_OPTS: [],
 *      TYPE: 'CODE',
 * }
 */
const STEP = {
    CODE: {
        COLOR: 'code',
        FLYOUT_TEMPLATE: 'CODE',
        ICON: <CodeIcon readOnly={ true }/>,
        ILLUSTRATION: <StepCodeIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: false,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'COPY', 'CUT', 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'CODE',
    },
    DECISION_V2: {
        COLOR: 'decision',
        FLYOUT_TEMPLATE: 'DECISION',
        ICON: <DecisionIcon readOnly={ true }/>,
        ILLUSTRATION: <StepDecisionIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'COPY', 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'DECISION_V2',
    },
    EXIT: {
        COLOR: 'exit',
        FLYOUT_TEMPLATE: null,
        ICON: <ExitIcon readOnly={ true }/>,
        ILLUSTRATION: <StepExitIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: true,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'SWAP', 'TEST' ],
        PATH_OPTS: [ 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [ 'EXIT', 'LINK', 'REDIRECT', 'RESOLUTION' ],
        TYPE: 'EXIT',
    },
    FALLBACK_SOURCE: {
        COLOR: 'source',
        FLYOUT_TEMPLATE: null,
        ICON: <SourceIcon readOnly={ true }/>,
        ILLUSTRATION: <StepSourceIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: false,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [],
        PATH_OPTS: [],
        SWAP_OPTS: [],
        TYPE: 'FALLBACK_SOURCE',
    },
    JUNCTION_PROCESSOR: {
        COLOR: 'junction',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <JunctionIcon readOnly={ true }/>,
        ILLUSTRATION: <StepJunctionIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: true,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'EDIT', 'SWAP', 'TEST' ],
        PATH_OPTS: [ 'JUNCTION' ],
        SWAP_OPTS: [ 'EXIT', 'LINK', 'REDIRECT' ],
        TYPE: 'JUNCTION_PROCESSOR',
    },
    JUNCTION_SOURCE: {
        COLOR: 'junction',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <JunctionIcon readOnly={ true }/>,
        ILLUSTRATION: <StepJunctionIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'EDIT', 'TEST' ],
        PATH_OPTS: [],
        SWAP_OPTS: [],
        TYPE: 'JUNCTION_SOURCE',
    },
    LINK: {
        COLOR: 'link',
        FLYOUT_TEMPLATE: null,
        ICON: <LinkIcon readOnly={ true }/>,
        ILLUSTRATION: <StepLinkIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: true,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'EDIT', 'SWAP', 'TEST' ],
        PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [ 'EXIT', 'LINK', 'REDIRECT', 'RESOLUTION' ],
        TYPE: 'LINK',
    },
    PAUSE: {
        COLOR: 'pause',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <TimeIcon readOnly={ true }/>,
        ILLUSTRATION: <StepLinkIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'COPY', 'CUT', 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'PAUSE',
    },
    REDIRECT: {
        COLOR: 'redirect',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <RedirectIcon readOnly={ true }/>,
        ILLUSTRATION: <StepLinkIllustration/>,  // TODO: replace w custom illustration...
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: true,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'EDIT', 'SWAP', 'TEST' ],
        PATH_OPTS: [ 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [ 'EXIT', 'LINK', 'REDIRECT', 'RESOLUTION' ],
        TYPE: 'REDIRECT',
    },
    REDIRECT_BY_PATH: {
        COLOR: 'redirect',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <RedirectIcon readOnly={ true }/>,
        ILLUSTRATION: <StepLinkIllustration/>,  // TODO: replace w custom illustration...
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: true,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'EDIT', 'SWAP', 'TEST' ],
        PATH_OPTS: [ 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [ 'EXIT', 'LINK', 'REDIRECT', 'RESOLUTION' ],
        TYPE: 'REDIRECT_BY_PATH',
    },
    REQUEST: {
        COLOR: 'request',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <RequestIcon readOnly={ true }/>,
        ILLUSTRATION: <StepRequestIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: true,
        IS_SYSTEM_STEP: false,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'COPY', 'CUT', 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'REQUEST',
    },
    RESOLUTION: {
        COLOR: 'resolution',
        FLYOUT_TEMPLATE: null,
        ICON: <ResolutionIcon readOnly={ true }/>,
        ILLUSTRATION: <StepResolutionIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: true,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'SWAP', 'TEST' ],
        PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [ 'EXIT', 'LINK', 'REDIRECT', 'RESOLUTION' ],
        TYPE: 'RESOLUTION',
    },
    REVIEW: {
        COLOR: 'review',
        FLYOUT_TEMPLATE: 'REVIEW',
        ICON: <ReviewsIcon readOnly={ true }/>,
        ILLUSTRATION: <StepReviewIllustration/>,
        INPUTS_HIDDEN: [ 'otherRequestFields', 'otherResponseFields' ],
        INPUTS_RESERVED: {
            ADDITIONAL_INFORMATION: 'otherRequestFields',
            SENDER: 'com.ymeadows.sender',
            CREATED_DATE: 'com.ymeadows.createdDate',
            SUBJECT: 'com.ymeadows.subject',
            HAS_ATTACHMENT: 'com.ymeadows.hasAttachment',
            PROPOSED_CHANGES: 'otherResponseFields',
            PROPOSED_OUTCOME: 'com.ymeadows.proposedOutcome',
            REQUEST_BODY: 'com.ymeadows.requestBody',
            RESPONSE_BODY: 'com.ymeadows.responseBody',
            TEMPLATE_VARIABLES: 'com.ymeadows.templateVariables',
        },
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'COPY', 'CUT', 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'JOURNEY' ],
        SWAP_OPTS: [],
        TYPE: 'REVIEW',
    },
    REVIEW_EXIT: {
        COLOR: 'exit',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <ExitIcon readOnly={ true }/>,
        ILLUSTRATION: <StepExitIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: true,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'EDIT', 'TEST' ],
        PATH_OPTS: [],
        SWAP_OPTS: [],
        TYPE: 'REVIEW_EXIT',
    },
    SOURCE: {
        COLOR: 'source',
        FLYOUT_TEMPLATE: 'SOURCE',
        ICON: <SourceIcon readOnly={ true }/>,
        ILLUSTRATION: <StepSourceIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'EDIT', 'TEST' ],
        PATH_OPTS: [],
        SWAP_OPTS: [],
        TYPE: 'SOURCE',
    },
    TEMPLATE: {
        COLOR: 'template',
        FLYOUT_TEMPLATE: 'RESPONSE',
        ICON: <TemplateIcon readOnly={ true }/>,
        ILLUSTRATION: <StepTemplateIllustration/>,
        INPUTS_HIDDEN: [ 'templateData' ],
        INPUTS_RESERVED: {
            TEMPLATE_INPUT: 'template',
            TEMPLATE_VARIABLES: 'templateData'
        },
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: true,
        IS_SYSTEM_STEP: false,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'COPY', 'CUT', 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'TEMPLATE',
    },
    TRANSFORM: {
        COLOR: 'transform',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <TransformIcon readOnly={ true }/>,
        ILLUSTRATION: <StepTransformIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: true,
        IS_SYSTEM_STEP: false,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'COPY', 'CUT', 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'TRANSFORM',
    },
    UPDATE_VARIABLE: {
        COLOR: 'review',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <VariableIcon readOnly={ true }/>,
        ILLUSTRATION: <WomanWithConstantIllustration />,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: true,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'COPY', 'CUT', 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'UPDATE_VARIABLE',
    },
    START_LOOP: {
        COLOR: 'transform',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <LoopStartIcon readOnly={ true }/>,
        ILLUSTRATION: null,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'START_LOOP',
    },
    END_LOOP: {
        COLOR: 'transform',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <LoopEndIcon readOnly={ true }/>,
        ILLUSTRATION: null,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: false,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [],
        PATH_OPTS: [ 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'END_LOOP',
    },
    START_ERROR_WATCH: {
        COLOR: 'errorWatch',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <ErrorWatchStartIcon readOnly={ true }/>,
        ILLUSTRATION: null,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'DELETE' ],
        PATH_OPTS: [ 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'START_ERROR_WATCH',
    },
    END_ERROR_WATCH: {
        COLOR: 'errorWatch',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <ErrorWatchEndIcon readOnly={ true }/>,
        ILLUSTRATION: null,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: false,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [],
        PATH_OPTS: [ 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'END_ERROR_WATCH',
    },
    ORDER_REVIEW: {
        COLOR: 'review',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <ReviewsIcon readOnly={ true }/>,
        ILLUSTRATION: <StepReviewIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [ 'COPY', 'CUT', 'DELETE', 'EDIT', 'TEST' ],
        PATH_OPTS: [ 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'ORDER_REVIEW',
    },
    UNKNOWN: {
        COLOR: 'disabled',
        FLYOUT_TEMPLATE: null,
        ICON: <UnknownIcon readOnly={ true }/>,
        ILLUSTRATION: <StepUnknownIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: true,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: true,
        MENU_OPTS: [ 'DELETE' ],
        PATH_OPTS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
        SWAP_OPTS: [],
        TYPE: 'UNKNOWN',
    },
    DEFAULTS: {
        COLOR: 'disabled',
        FLYOUT_TEMPLATE: 'DEFAULT',
        ICON: <UnknownIcon readOnly={ true }/>,
        ILLUSTRATION: <ManWaitingIllustration/>,
        INPUTS_HIDDEN: [],
        INPUTS_RESERVED: null,
        IS_AVAILABLE_IN_LIBRARY: true,
        IS_CREATABLE: false,
        IS_SYSTEM_STEP: false,
        IS_TERMINATION_STEP: false,
        IS_VIRTUAL_STEP: false,
        MENU_OPTS: [],
        PATH_OPTS: [],
        SWAP_OPTS: [],
        TYPE: 'UNKNOWN',
    },
};


export default {
    DEFAULT_LIBRARY_VIEW: 'grid',
    FALLBACK_STEPS: _getStepsByPath('FALLBACK'),
    JOURNEY_STEPS: _getStepsByPath('JOURNEY'),
    JUNCTION_STEPS: _getStepsByPath('JUNCTION'),
    NATIVE_INPUTS: [ 'BOOLEAN', 'PLAIN_TEXT', 'ARRAY', 'NUMBER' ],
    PATHS: [ 'FALLBACK', 'JOURNEY', 'JUNCTION' ],
    STEP,
    SYSTEM_STEPS: _getStepsByKey('IS_SYSTEM_STEP'),
    TERMINATION_STEPS: _getStepsByKey('IS_TERMINATION_STEP'),
    VIRTUAL_STEPS: _getStepsByKey('IS_VIRTUAL_STEP'),
};



//*************************************************
//*****   Helper Functions   **********************
//*************************************************
/**
 * _getStepsByPath
 * desc: returns a list of step names
 * note: used to determine library step options
 * @param   {string} pathType  Name of path type
 * @returns {array}            List of step names
 */
function _getStepsByPath(pathType) {
    let stepsList = [];
    Object.values(STEP).forEach(x => {
        if (x.PATH_OPTS.includes(pathType)) stepsList.push(x.TYPE);
    });
    return stepsList;
}


/**
 * _getStepsByKey
 * desc: returns a list of step names
 * note: used to determine library step options
 * @param   {string} key  Name of object key
 * @returns {array}       List of step names
 */
function _getStepsByKey(key) {
    let stepsList = [];
    Object.values(STEP).forEach(x => {
        if (x[key]) stepsList.push(x.TYPE);
    });
    return stepsList;
}
